// Componente per il modello 3D
import { useGLTF, useAnimations } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { 
    Vector3, 
    Box3, 
    Box3Helper, 
    Mesh, 
    SkinnedMesh, 
    Bone, 
    LoopOnce, 
    Color,
    CustomBlending,
    ShaderMaterial,
    MeshPhysicalMaterial,
    SrcAlphaFactor,
    OneMinusSrcAlphaFactor,
    OneFactor,
    RepeatWrapping,
    UniformsUtils
} from "three";
import { useMemo, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { SubsurfaceScatteringShader } from "three/examples/jsm/shaders/SubsurfaceScatteringShader.js";

import MaterialController from "./MaterialController";
import { setMaterialNamesList, setMaterialConfigsState } from '../../../redux/model/actions';

export default function Model({
    url,                     // URL del modello 3D
    modelPresets,            // Preset dei materiali del modello (IOR, transmission, ecc.)
    onError,                 // Callback per gestire gli errori
    handleHasAnimations,     // Callback per gestire le animazioni
    inDebugMode = false,     // Flag per la modalità debug
    isAnimatedOn,            // Flag per l'animazione attiva
    isFirstAnimation,        // Flag per la prima animazione
    isModelPlacedInAR = false, // Flag per il posizionamento in AR
    setModelInfo,             // Callback per settare le info del modello
    hasInteracted             // Flag per indicare se l'utente ha interagito con il modello
}) {
    const dispatch = useDispatch();
    // useRef della mappa per salvare i materiali originali
    const originalMaterialsMap = useRef(new Map());
    // useRef della mappa per salvare i materiali con preset
    const materialsWithPresetMap = useRef(new Map());
    // useRef della mappa per salvare i materiali migliorati per la visualizzazione
    const adjustedMaterialsMap = useRef(new Map());
    
    // Ref per il modello da animare
    const modelRef = useRef();
    
    // Stati per l'animazione di feedback
    const [showFeedbackAnimation, setShowFeedbackAnimation] = useState(false);
    const feedbackAnimationStartTime = useRef(0);
    const activeTimeoutsRef = useRef([]);

    // Stato per le configurazioni dei materiali
    const { materialConfigsState } = useSelector((state) => state.model);

    // Chiavi che influenzano gli shader
    const shaderAffectingKeys = [
        'side', 'blending', 'blendSrc', 'blendDst', 'blendEquation',
        'transparent', 'depthTest', 'depthWrite', 'alphaTest'
    ];

    // Carica il modello 3D
    const { scene, animations } = useGLTF(url, undefined, (error) => {
        console.error('Error loading the model:', error);
        onError(error.message);
    });

    // Salva i materiali originali
    if (scene && originalMaterialsMap.current.size === 0) {
        scene.traverse((child) => {
            if (child instanceof Mesh || child instanceof SkinnedMesh) {
                if (child.material && !originalMaterialsMap.current.has(child.material.name)) {
                    // Salva SEMPRE il clone originale
                    originalMaterialsMap.current.set(child.material.name, child.material.clone());
                }
            }
        });
        // console.log('originalMaterialsMap', originalMaterialsMap.current);
    }

    // Assegna un nome unico al modello per facilitare la sua identificazione
    if (scene.name === '' || scene.name === 'AuxScene') {
        scene.name = 'Scene';
    }
    if (animations) {
        scene.animations = animations;
    }

    // Effetto per calcolare e settare le informazioni del modello (dimensioni e centro)
    useEffect(() => {
        if (!setModelInfo) return;

        if (scene) {
            const boundingBox = new Box3().setFromObject(scene);
            const bbSize = boundingBox.getSize(new Vector3());
            const bbCenter = boundingBox.getCenter(new Vector3());

            setModelInfo({
                sizes: { x: bbSize.x, y: bbSize.y, z: bbSize.z },
                center: { x: bbCenter.x, y: bbCenter.y, z: bbCenter.z }
            });
        }
    }, [scene, setModelInfo]);

    // Effetto per fermare l'animazione quando l'utente interagisce
    useEffect(() => {
        if (hasInteracted) {
            setShowFeedbackAnimation(false);
            
            // Pulisce tutti i timeout pendenti
            activeTimeoutsRef.current.forEach(timeoutId => {
                clearTimeout(timeoutId);
            });
            activeTimeoutsRef.current = [];
        }
    }, [hasInteracted]);

    // Effetto per gestire il ciclo dell'animazione di feedback
    useEffect(() => {
        if (!scene || isModelPlacedInAR || hasInteracted) return;

        const startAnimationCycle = () => {
            // Delay iniziale di 1 secondo
            const initialDelay = setTimeout(() => {
                if (hasInteracted) return; // Check se l'utente ha interagito nel frattempo
                
                // Avvia l'animazione di feedback per 3 secondi
                setShowFeedbackAnimation(true);
                feedbackAnimationStartTime.current = Date.now();
                
                const animationTimeout = setTimeout(() => {
                    if (hasInteracted) return; // Check se l'utente ha interagito nel frattempo
                    
                    setShowFeedbackAnimation(false);
                    
                    // Riavvia il ciclo dopo 2 secondi di pausa
                    const cycleTimeout = setTimeout(() => {
                        if (!hasInteracted) {
                            startAnimationCycle();
                        }
                    }, 2000);
                    
                    activeTimeoutsRef.current.push(cycleTimeout);
                }, 3000); // 3 secondi di animazione
                
                activeTimeoutsRef.current.push(animationTimeout);
            }, 2000); // 1 secondo di delay iniziale
            
            activeTimeoutsRef.current.push(initialDelay);
        };

        startAnimationCycle();
        
        return () => {
            // Pulisce tutti i timeout quando il componente si smonta o le dipendenze cambiano
            activeTimeoutsRef.current.forEach(timeoutId => {
                clearTimeout(timeoutId);
            });
            activeTimeoutsRef.current = [];
        };
    }, [scene, isModelPlacedInAR, hasInteracted]);

    // Animazione di feedback per rendere il modello interagibile
    useFrame((state) => {
        if (showFeedbackAnimation && modelRef.current) {
            const elapsedTime = (Date.now() - feedbackAnimationStartTime.current) / 1000;
            
            // Rotazione dolce sull'asse Y
            modelRef.current.rotation.y = Math.sin(elapsedTime * 2) * 0.3; // Oscillazione di ±0.3 radianti
            
        } else if (modelRef.current && !showFeedbackAnimation) {
            // Riporta gradualmente il modello alla posizione originale
            modelRef.current.rotation.y *= 0.95;
            
            // Se i valori sono molto piccoli, imposta a zero
            if (Math.abs(modelRef.current.rotation.y) < 0.001) {
                modelRef.current.rotation.y = 0;
            }
        }
    });

    // Effetto per visualizzare il Box3Helper se inDebugMode
    // useEffect(() => {
    //     if (!scene || !inDebugMode) return;
    //     // Crea il bounding box
    //     const boundingBox = new Box3().setFromObject(scene);
    //     const helper = new Box3Helper(boundingBox, 0xff0000);
    //     scene.add(helper);
    //     // Cleanup: rimuovi helper
    //     return () => {
    //         scene.remove(helper);
    //         helper.geometry.dispose();
    //         helper.material.dispose();
    //     };
    // }, [scene, inDebugMode]);

    // Gestione delle animazioni
    const { actions, names } = useAnimations(animations, scene);

    // funzione di reset animazioni
    scene.resetAllAnimations = () => {
        if (!actions) return;
        Object.values(actions).forEach(action => {
            action.reset();
            action.stop();
        });
    };

    // funzione di inizializzazione animazioni
    scene.initAllAnimations = () => {
        if (!actions) return;
        Object.values(actions).forEach(action => {
            action.play();
            action.paused = true;
        });
    };

    // funzione utility per ottenere il materiale originale, se quello in input è stato modificato
    scene.getOriginalMaterial = (mat) => {
        if (originalMaterialsMap.current.has(mat.name)) {
            return originalMaterialsMap.current.get(mat.name).clone();
        }
        return null;
    };

    // Effetto per la pulizia delle risorse quando il componente viene smontato
    useEffect(() => {
        return () => {
            // Pulisci tutti i timeout dell'animazione di feedback
            activeTimeoutsRef.current.forEach(timeoutId => {
                clearTimeout(timeoutId);
            });
            activeTimeoutsRef.current = [];
            
            if (scene) {
                disposeScene(scene);
                scene.clear();
            }

            // Pulisci i materiali nella mappa
            materialsMap.forEach((material) => {
                disposeMaterial(material);
            });
            materialsMap.clear();

            if (originalMaterialsMap.current.size > 0) {
                originalMaterialsMap.current.forEach((material) => {
                    disposeMaterial(material);
                });
                originalMaterialsMap.current.clear();
            }
            if (materialsWithPresetMap.current.size > 0) {
                materialsWithPresetMap.current.forEach((material) => {
                    disposeMaterial(material);
                });
                materialsWithPresetMap.current.clear();
            }
            if (adjustedMaterialsMap.current.size > 0) {
                adjustedMaterialsMap.current.forEach((material) => {
                    disposeMaterial(material);
                });
                adjustedMaterialsMap.current.clear();
            }

            // Pulisci la cache
            // THREE.Cache.remove(url);
            useGLTF.clear(url);
        };
    }, [url]);

    // Configurazione iniziale delle animazioni
    useEffect(() => {
        if (animations && names.length > 0) {
            const action = actions[names[0]];

            // Configura l'animazione per fermarsi all'ultimo frame
            action.clampWhenFinished = true;
            action.loop = LoopOnce;
            if (names[0].includes('explosion_') && action.getClip().duration > 5) {
                // console.log('clip duration before setDuration', action.getClip().duration);
                action.getClip().duration = action.getClip().duration * 0.5;
                // console.log('clip duration after setDuration', action.getClip().duration);
            }

            // Notifica il parent che abbiamo un'animazione
            handleHasAnimations(true, names[0], action.getClip().duration);

            // Metti in pausa l'animazione al primo frame
            action.play();
            action.paused = true;

            // Disabilita il culling per le mesh skinned, per evitare il non rendering delle mesh aninate
            scene.traverse((child) => {                 
                if (child.isSkinnedMesh) {
                    child.frustumCulled = false;
                }
            });
        }
    }, [url, animations]);

    // Gestione dell'animazione
    useEffect(() => {
        const action = actions[names[0]];
        if (action) {
            if (isAnimatedOn) {
                if (isFirstAnimation) {
                    action.timeScale = 1;
                    action.reset();
                } else {
                    action.timeScale = -1;
                }
                // console.log(action.time);
                action.paused = false;
                action.play();
            } else {
                // console.log(action.time);
                action.paused = true;
            }
        }
    }, [isAnimatedOn, isFirstAnimation]);

    // Creazione del dizionario dei materiali
    const materialsMap = useMemo(() => {
        const map = new Map();

        scene.traverse((child) => {
            if (child instanceof Bone) return;

            if (child instanceof Mesh || child instanceof SkinnedMesh) {
                if (child.material && !map.has(child.material.name)) {
                    const matClone = child.material.clone();
                    // Rifiniture al materiale se è metallo o trasmissivo 
                    adjustMaterials(map, matClone);
                    // Aggiunge il materiale alla mappa
                    map.set(child.material.name, matClone);
                }
            }
        });

        // console.log('adjustedMaterialsMap', adjustedMaterialsMap.current);

        return map;
    }, [scene]);

    // useEffect per aggiornare la lista dei materiali
    useEffect(() => {
        // Converti la Map in un array per serializzarla
        dispatch(setMaterialNamesList(Array.from(materialsMap.keys())));
    }, [materialsMap, dispatch]);

    // useEffect per applicare i materiali in materialsMap al modello 3D
    useEffect(() => {
        if (!scene) return;

        if (materialsMap.size > 0) {
            scene.traverse((child) => {
                if (child instanceof Mesh || child instanceof SkinnedMesh) {
                    if (child.material && materialsMap.has(child.material.name)) {
                        child.material = materialsMap.get(child.material.name);
                    }
                }
            });
        }

        // console.log('materialsMap', materialsMap);
    }, [scene]);

    // useEffect per applicare o rimuovere i preset ai materiali
    useEffect(() => {
        if (!scene || !modelPresets || modelPresets.size === 0) return;
        scene.traverse((child) => {
            if (child instanceof Bone) return;

            if (child instanceof Mesh || child instanceof SkinnedMesh) {
                if (!child.material) return;

                if (Array.isArray(child.material)) {
                    child.material = child.material.map(material => {
                        return getMaterialWithPreset(material);
                    });
                } else {
                    const material = child.material;
                    const materialWithPreset = getMaterialWithPreset(material);
                    if (materialWithPreset !== material) {
                        child.material = materialWithPreset;
                    }
                }
            }
        });

        // Cleanup function per ripristinare i materiali originali
        return () => {
            scene.traverse(child => {
                if (child instanceof Mesh || child instanceof SkinnedMesh) {
                    const material = child.material;
                    if (Array.isArray(material)) {
                        child.material = material.map(getRestoredMaterial);
                    } else {
                        child.material = getRestoredMaterial(material);
                    }
                }
            });
            // Dispose di tutti i materiali con preset
            for (const mat of materialsWithPresetMap.current.values()) {
                disposeMaterial(mat);
            }
            materialsWithPresetMap.current.clear();
        };
    }, [modelPresets, scene]);

    // Gestione delle ombre in modalità AR
    useEffect(() => {
        if (isModelPlacedInAR && scene) {
            scene.traverse((child) => {
                if (child.isMesh) {
                    child.castShadow = true;
                }
            });
        }
    }, [scene, isModelPlacedInAR]);

    // useEffect per switch dei materiali trasmissivi e subsurface scattering
    useEffect(() => {
        if (!inDebugMode) return;

        Object.entries(materialConfigsState).forEach(([name, config]) => {
            if (name.endsWith('_SSS')) {
                if (config.subSurfaceScattering === true) {
                    const baseMaterialName = name.replace('_SSS', '');
                    const baseMaterial = materialsMap.get(baseMaterialName);
                    const sssMaterial = materialsMap.get(name);

                    if (baseMaterial && sssMaterial) {
                        // Trova tutti i mesh che usano questi materiali
                        scene.traverse((child) => {
                            if (child instanceof Mesh || child instanceof SkinnedMesh) {
                                if (child.material.name === baseMaterialName) {
                                    child.material = sssMaterial;
                                }
                            }
                        });
                    }
                } else {
                    const baseMaterialName = name.replace('_SSS', '');
                    const baseMaterial = materialsMap.get(baseMaterialName);

                    if (baseMaterial) {
                        scene.traverse((child) => {
                            if (child instanceof Mesh || child instanceof SkinnedMesh) {
                                if (child.material.name === name) {
                                    child.material = baseMaterial;
                                }
                            }
                        });
                    }
                }
            }
        });
    }, [materialConfigsState, inDebugMode, scene, materialsMap]);

    // Funzione helper per applicare un preset ad un materiale
    function getMaterialWithPreset(material) {
        const materialName = material.name;
        const materialPreset = modelPresets.get(materialName);

        if (materialPreset) {
            let updatedMaterial;
            if (materialsWithPresetMap.current.has(materialName)) {
                updatedMaterial = materialsWithPresetMap.current.get(materialName);
            } else {
                updatedMaterial = applyMaterialPreset(material, materialPreset);
                materialsWithPresetMap.current.set(materialName, updatedMaterial);
            }
            return updatedMaterial;
        }

        return material;
    }

    // Funzione helper per ripristinare un materiale
    function getRestoredMaterial(mat) {
        if (materialsWithPresetMap.current.has(mat.name)) {
            if (adjustedMaterialsMap.current.has(mat.name)) {
                return adjustedMaterialsMap.current.get(mat.name);
            } else if (materialsMap.has(mat.name)) {
                return materialsMap.get(mat.name);
            } else if (originalMaterialsMap.current.has(mat.name)) {
                return originalMaterialsMap.current.get(mat.name);
            }
        }
        return mat
    }

    // Funzione per aggiornare le configurazioni dei materiali
    const handleMaterialConfigUpdate = useCallback((name, config) => {
        dispatch(setMaterialConfigsState(name, config));

        if (inDebugMode && materialsMap && materialsMap.has(name)) {
            const material = materialsMap.get(name);
            if (material) {
                applyMaterialConfig(material, config);
            }
        }
    }, []);

    // Funzione per applicare un preset al materiale relativo
    function applyMaterialPreset(material, preset) {
        if (!originalMaterialsMap.current.has(material.name)) {
            originalMaterialsMap.current.set(material.name, material);
        }

        let materialCopy;
        // Se è una lente non PBR
        if (material.name.startsWith('LN_') && !material.isMeshPhysicalMaterial) {
            materialCopy = createTransmissiveLenses(material);
        } else {
            // Crea una copia del materiale originale per le modifiche
            materialCopy = material.clone();
        }

        let hasUpdated = false;

        Object.entries(preset).forEach(([key, value]) => {
            if (key !== 'id' && key !== 'type' && key !== 'materialName' && key !== 'name' && key !== 'newMaterialName') {
                if (key in materialCopy) {
                    if (materialCopy[key] instanceof Color) {
                        materialCopy[key].set(value);
                    } else {
                        materialCopy[key] = value;
                        if (shaderAffectingKeys.includes(key) && !hasUpdated) {
                            hasUpdated = true;
                        }
                    }
                }
            }
        });

        if (hasUpdated) {
            materialCopy.needsUpdate = true;
        }
        return materialCopy;
    }

    // Funzione per creare un materiale trasmissivo per le lenti, partendo da quello di base
    function createTransmissiveLenses(baseMaterial) {
        const newMaterial = new MeshPhysicalMaterial({
            name: baseMaterial.name, //+ '_TR', // nuovo nome
            color: baseMaterial.color,
            map: baseMaterial.map,
            roughnessMap: baseMaterial.roughnessMap,
            metalnessMap: baseMaterial.metalnessMap,
            normalMap: baseMaterial.normalMap,
            alphaMap: baseMaterial.alphaMap,
            roughness: baseMaterial.roughness,
            metalness: baseMaterial.metalness,
            alphaTest: baseMaterial.alphaTest,
            side: baseMaterial.side,
            opacity: baseMaterial.opacity,
            emissive: baseMaterial.emissive.clone?.() || baseMaterial.emissive,
            emissiveIntensity: baseMaterial.emissiveIntensity,
            depthWrite: baseMaterial.depthWrite,
            depthTest: baseMaterial.depthTest,
            depthFunc: baseMaterial.depthFunc,
            ior: 1.03,
            transparent: false,
            blending: CustomBlending,
            blendSrc: SrcAlphaFactor,
            blendDst: OneMinusSrcAlphaFactor,
        });
        return newMaterial;
    }

    // Funzione per regolare i materiali metallici e trasmissivi 
    function adjustMaterials(map, material) {
        let materialHasChanged = false;
        // Caso materiale metallico
        if (material.name.startsWith('MT_') && material.emissiveIntensity > 0) {
            material.emissiveIntensity = 0;
            materialHasChanged = true;
        }

        // Caso materiale trasmissivo
        if (material.name.startsWith('TR_')) {
            material.transparent = true;
            material.blending = CustomBlending;
            material.blendSrc = OneFactor;
            if (material.opacity < 1) {
                material.opacity = 1;
            }

            // Questo if è per generare un subsurface scattering material da un materiale trasmissivo
            // if (!map.has(material.name + '_SSS')) {
            //     const sssMaterial = createSubsurfaceScatteringMaterial(material);
            //     if (sssMaterial) {
            //         map.set(sssMaterial.name, sssMaterial);
            //     }
            // }
            materialHasChanged = true;
        }

        // Caso tutti i materiali non lenti che hanno la sheen
        if (!material.name.startsWith('LN_')) {
            if (material.sheen > 0.5) {
                material.sheen = 0.5;
                materialHasChanged = true;
            }
        } else {
            if (!material.name.includes('_TRS_') && !material.depthWrite) {
                material.depthWrite = true;
                materialHasChanged = true;
            }
        }
        if (materialHasChanged && !adjustedMaterialsMap.current.has(material.name)) {
            adjustedMaterialsMap.current.set(material.name, material);
        }
    }

    // Funzione per applicare le configurazioni dei materiali
    function applyMaterialConfig(material, config) {
        let hasUpdated = false;

        // Gestione speciale per i materiali SSS
        if (material.name.endsWith('_SSS')) {
            Object.entries(config).forEach(([key, value]) => {
                // Gestione degli uniforms specifici per SSS
                if (key === 'distortion') {
                    material.uniforms.thicknessDistortion.value = value;
                    hasUpdated = true;
                } else if (key === 'ambient') {
                    material.uniforms.thicknessAmbient.value = value;
                    hasUpdated = true;
                } else if (key === 'attenuation') {
                    material.uniforms.thicknessAttenuation.value = value;
                    hasUpdated = true;
                } else if (key === 'power') {
                    material.uniforms.thicknessPower.value = value;
                    hasUpdated = true;
                } else if (key === 'scale') {
                    material.uniforms.thicknessScale.value = value;
                    hasUpdated = true;
                }
                else if (key === 'diffuse') {
                    material.uniforms.diffuse.value = value;
                    hasUpdated = true;
                }
                else if (key === 'thicknessColor') {
                    material.uniforms.thicknessColor.value = value;
                    hasUpdated = true;
                }
                else if (key === 'ior') {
                    material.uniforms.ior.value = value;
                    hasUpdated = true;
                }
            });
        } else {
            // Gestione normale per gli altri materiali
            Object.entries(config).forEach(([key, value]) => {
                if (key in material) {
                    if (material[key] instanceof Color) {
                        material[key].set(value);
                    } else {
                        material[key] = value;
                        if (shaderAffectingKeys.includes(key) && !hasUpdated) {
                            hasUpdated = true;
                        }
                    }
                }
            });
        }

        if (hasUpdated) {
            material.needsUpdate = true;
        }
    }

    // Funzione per generare materiali subsurface scattering da materiale trasmissivo di base
    function createSubsurfaceScatteringMaterial(baseMaterial) {
        const imgTexture = baseMaterial.map;
        if (!imgTexture) return;
        imgTexture.wrapS = imgTexture.wrapT = RepeatWrapping;

        const thicknessTexture = baseMaterial.thicknessMap;

        const shader = SubsurfaceScatteringShader;
        const uniforms = UniformsUtils.clone(shader.uniforms);
        // console.log(uniforms);

        uniforms['map'].value = imgTexture;

        uniforms['diffuse'].value = new Vector3(1.0, 0.2, 0.2);
        uniforms['shininess'].value = 500;

        uniforms['thicknessMap'].value = thicknessTexture ? thicknessTexture : imgTexture;
        uniforms['thicknessColor'].value = new Vector3(0.5, 0.3, 0.0);
        uniforms['thicknessDistortion'].value = 0.1;
        uniforms['thicknessAmbient'].value = 0.4;
        uniforms['thicknessAttenuation'].value = 0.8;
        uniforms['thicknessPower'].value = 2.0;
        uniforms['thicknessScale'].value = 16.0;

        uniforms['ior'].value = baseMaterial.ior;

        const newMaterial = new ShaderMaterial({
            uniforms: uniforms,
            vertexShader: shader.vertexShader,
            fragmentShader: shader.fragmentShader,
            lights: true,
            name: baseMaterial.name + '_SSS',
            // transparent: true,
            // blending: THREE.NormalBlending,
            // depthWrite: false
        });

        // console.log(newMaterial);

        return newMaterial;
    }

    // Funzione per eliminare i materiali dalla memoria
    function disposeMaterial(material) {
        if (Array.isArray(material)) {
            material.forEach(m => disposeMaterial(m));
        } else {
            // Dispose di tutte le texture del materiale
            if (material.map) material.map.dispose();
            if (material.lightMap) material.lightMap.dispose();
            if (material.aoMap) material.aoMap.dispose();
            if (material.emissiveMap) material.emissiveMap.dispose();
            if (material.bumpMap) material.bumpMap.dispose();
            if (material.normalMap) material.normalMap.dispose();
            if (material.displacementMap) material.displacementMap.dispose();
            if (material.roughnessMap) material.roughnessMap.dispose();
            if (material.metalnessMap) material.metalnessMap.dispose();
            if (material.alphaMap) material.alphaMap.dispose();
            if (material.envMap) material.envMap.dispose();
            if (material.anisotropyMap) material.anisotropyMap.dispose();
            if (material.clearcoatMap) material.clearcoatMap.dispose();
            if (material.clearcoatNormalMap) material.clearcoatNormalMap.dispose();
            if (material.clearcoatRoughnessMap) material.clearcoatRoughnessMap.dispose();
            if (material.iridescenceMap) material.iridescenceMap.dispose();
            if (material.iridescenceThicknessMap) material.iridescenceThicknessMap.dispose();
            if (material.sheenRoughnessMap) material.sheenRoughnessMap.dispose();
            if (material.sheenColorMap) material.sheenColorMap.dispose();
            if (material.specularIntensityMap) material.specularIntensityMap.dispose();
            if (material.specularColorMap) material.specularColorMap.dispose();
            if (material.thicknessMap) material.thicknessMap.dispose();
            if (material.transmissionMap) material.transmissionMap.dispose();

            material.dispose();
        }
    }

    // Funzione per eliminare la scena dalla memoria
    function disposeScene(scene) {
        if (!scene) return;

        scene.traverse((child) => {
            // Dispose della geometria
            if (child.geometry) {
                child.geometry.dispose();
                child.geometry = null;
            }

            // Dispose del materiale
            if (child.material) {
                disposeMaterial(child.material);
            }

            // Dispose dello skeleton
            if (child.skeleton) {
                child.skeleton.dispose();
                if (child.skeleton.boneInverses) {
                    child.skeleton.boneInverses.length = 0;
                }
                if (child.skeleton.boneMatrices) {
                    child.skeleton.boneMatrices = null;
                }
                if (child.skeleton.boneTexture) {
                    child.skeleton.boneTexture.dispose();
                    child.skeleton.boneTexture = null;
                }
            }
        });

        // Elimina le animazioni
        if (scene.animations) {
            scene.animations.length = 0;
        }
    }

    return (
        <>
            {/* Controller dei materiali in modalità debug */}
            {inDebugMode && Array.from(materialsMap.entries()).map(([name, material]) => (
                <MaterialController
                    key={name}
                    name={name}
                    material={material}
                    onUpdate={handleMaterialConfigUpdate}
                />
            ))}

            {/* Renderizza il modello 3D */}
            <primitive ref={modelRef} object={scene}/>    
        </>
    );
};